import React, { useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import './Demo.css';

const demoGifs = [
  {
    title: 'Your Compliance Expert',
    heading: 'Instant, cited global regulatory answers',
    description: 'Forget manual research and costly consultants. Ask any question across architectural policy, technical guidance or building codes and instantly get precise, fully cited clauses with highlighted citations, formatted for direct use in specifications and tender documents.',
    src: '/Your Compliance Expert.png',
    alt: 'AI Workflow Demo',
  },
  {
    title: 'Your Project Specialist',
    heading: 'Context-aware project insights',
    description: 'Archaas learns your brief, site conditions, client history and design intent to deliver tailored approval checks and recommendations that fit your exact context – so you prevent scope creep, avoid revisions and deliver proposals that align first time.',
    src: '/Your Project Specialist.png',
    alt: 'AI Workflow Demo',
  },
  {
    title: 'Your Integrated Tool',
    heading: 'Search less, deliver faster',
    description: 'Replace hundreds of open tabs and manual admin with one secure, integrated search across inboxes, drives, team notes and external research. You can find the evidence behind any decision, keep teams aligned and close approvals sooner.',
    src: '/Your Integrated Tool.png',
    alt: 'Integration Demo',
  },
  {
    title: 'Your Practice Hub',
    heading: 'Office standards embedded',
    description: 'Train a single, enterprise-grade AI on your practice standards, libraries and systems so every project inherits your policies and design principles, enabling faster onboarding, consistent delivery and secure protection of your intellectual property.',
    src: '/Your Practice Hub.png',
    alt: 'Collaboration Demo',
  },
  {
    title: 'Your Architecture Co-Pilot',
    heading: 'Master architectural intelligence in minutes',
    description: 'Compliance errors, client delays and disconnected systems drain your time. Archaas unifies regulations, practice standards and project intelligence in one auditable, secure workspace so you spot risks early, reduce rework and protect your fees.',
    src: '/Your Architecture Co-Pilot.png',
    alt: 'Overview Demo',
  },
];

const Demo: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showZoomTooltip, setShowZoomTooltip] = useState(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = React.useRef<HTMLElement>(null);

  // Check if mobile on mount and resize
  // Breakpoint accounts for 132% image scaling + 80% container: 850 * 1.32 / 0.8 ≈ 1200px
  React.useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      const shouldBeMobile = width <= 1200;
      setIsMobile(shouldBeMobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when modal is open on mobile
  React.useEffect(() => {
    if (isModalOpen && isMobile) {
      // Simply prevent scroll without moving the page
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore body scroll
        document.body.style.overflow = '';
      };
    }
  }, [isModalOpen, isMobile]);

  // Intersection Observer to detect when section is in view
  React.useEffect(() => {
    const section = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.3, // Trigger when 30% of the section is visible
      }
    );

    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  // Auto-scroll through categories on desktop only when section is in view (5 seconds per category)
  React.useEffect(() => {
    if (!isMobile && autoScrollEnabled && isInView) {
      const interval = setInterval(() => {
        setActiveIndex((prevIndex) => (prevIndex + 1) % demoGifs.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [isMobile, autoScrollEnabled, isInView]);

  const openModal = () => {
    setIsModalOpen(true);
    setShowZoomTooltip(true);
    setAutoScrollEnabled(false);
    // Hide tooltip after 2 seconds
    setTimeout(() => {
      setShowZoomTooltip(false);
    }, 2000);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setShowZoomTooltip(false);
  };

  const nextSlide = () => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % demoGifs.length);
  };

  const prevSlide = () => {
    setActiveIndex((prevIndex) => (prevIndex - 1 + demoGifs.length) % demoGifs.length);
  };

  const mainSwipeHandlers = useSwipeable({
    onSwipedLeft: nextSlide,
    onSwipedRight: prevSlide,
    trackMouse: true,
    preventScrollOnSwipe: false,
    trackTouch: true
  });

  const modalSwipeHandlers = useSwipeable({
    onSwipedLeft: nextSlide,
    onSwipedRight: prevSlide,
    trackMouse: true,
    preventScrollOnSwipe: false,
    trackTouch: true
  });

  return (
    <section className="demo-section" id="demo" ref={sectionRef}>
      <div className="features-container">
        <h2 className="demo-title">Architectural intelligence starts here.</h2>
        
        {isMobile ? (
          <div className="demo-mobile-wrapper" data-view="mobile">
            {/* Mobile: Page indicators */}
            <div className="demo-page-indicators">
              {demoGifs.map((_, index) => (
                <button
                  key={index}
                  className={`demo-page-dot ${activeIndex === index ? 'active' : ''}`}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
            <div className="demo-swipe-hint">Swipe to explore →</div>
            
            {/* Mobile: Swipeable content */}
            <div className="demo-mobile-container" {...mainSwipeHandlers} key={`swipe-${isModalOpen}`}>
              <div className="demo-mobile-slide" onClick={openModal} style={{ cursor: 'pointer' }}>
                <h3 className="demo-mobile-title">{demoGifs[activeIndex].title}</h3>
                <h4 className="demo-mobile-heading">{demoGifs[activeIndex].heading}</h4>
                <p className="demo-mobile-description">{demoGifs[activeIndex].description}</p>
                <div className="demo-mobile-image">
                  <img
                    src={demoGifs[activeIndex].src}
                    alt={demoGifs[activeIndex].alt}
                    className="demo-mobile-gif"
                  />
                </div>
                <div className="demo-click-to-zoom-hint">Click to open</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="demo-desktop-wrapper" data-view="desktop">
            {/* Desktop: Original layout */}
            <div className="demo-titles">
              {demoGifs.map((demo, idx) => (
                <button
                  key={demo.title}
                  className={`demo-title-btn${activeIndex === idx ? ' active' : ''}`}
                  onClick={() => {
                    setActiveIndex(idx);
                    setAutoScrollEnabled(false);
                  }}
                >
                  {demo.title}
                </button>
              ))}
            </div>
            <div className="demo-video-container active">
              <div className="demo-video-grid">
                <div className="demo-gif-text">
                  <h3 className="demo-gif-heading">{demoGifs[activeIndex].heading}</h3>
                  <p className="demo-gif-description">{demoGifs[activeIndex].description}</p>
                </div>
                <div className="demo-gif-image">
                  <img
                    src={demoGifs[activeIndex].src}
                    alt={demoGifs[activeIndex].alt}
                    className="demo-gif"
                    onClick={openModal}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="demo-popup-overlay" onClick={closeModal}>
          <div className="demo-popup-container" onClick={(e) => e.stopPropagation()} {...(isMobile ? modalSwipeHandlers : {})}>
            <button className="demo-popup-close" onClick={closeModal}>
              ×
            </button>
            {isMobile && (
              <div className="demo-popup-page-indicators">
                {demoGifs.map((_, index) => (
                  <button
                    key={index}
                    className={`demo-popup-page-dot ${activeIndex === index ? 'active' : ''}`}
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
            <div className="demo-popup-content">
              {isMobile && (
                <div className="demo-popup-text">
                  <div>
                    <h3 className="demo-popup-heading">{demoGifs[activeIndex].heading}</h3>
                    <p className="demo-popup-description">{demoGifs[activeIndex].description}</p>
                  </div>
                  <div className="demo-popup-nav">
                    {demoGifs.map((demo, idx) => (
                      <button
                        key={demo.title}
                        className={`demo-popup-nav-btn${activeIndex === idx ? ' active' : ''}`}
                        onClick={() => setActiveIndex(idx)}
                      >
                        {demo.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="demo-popup-image-wrapper">
                <TransformWrapper
                  initialScale={1}
                  minScale={1}
                  maxScale={isMobile ? 3 : 1}
                  centerOnInit={true}
                  wheel={{ disabled: true }}
                  panning={{ disabled: !isMobile }}
                  doubleClick={{ disabled: true }}
                >
                  <TransformComponent
                    wrapperStyle={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    contentStyle={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={demoGifs[activeIndex].src}
                      alt={demoGifs[activeIndex].alt}
                      className="demo-popup-image"
                    />
                  </TransformComponent>
                </TransformWrapper>
                {showZoomTooltip && isMobile && (
                  <div className="demo-zoom-tooltip">
                    Pinch to zoom
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Demo; 